import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Avatar,
    TablePagination,
    Box,
    Card,
    Grid,
    Chip,
    Skeleton,
    Stack,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
} from "@mui/material";
import { contestServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import { capitalizeFirstLetter } from "../../utils/helpers";
import { formatCash, formatPrice } from "../../utils/formatNumber";
import CardLayout from "../../layouts/CardLayout";
import SDCoinIcon from "../../components/shared/SDCoinIcon";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DiamondIcon from "@mui/icons-material/Diamond";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import ReplayIcon from "@mui/icons-material/Replay";
import FlagIcon from "@mui/icons-material/Flag";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";

const MEDAL_COLORS = { 1: "#FFB400", 2: "#A0A7B0", 3: "#CD7F32" };

const STATUS_CHIP = {
    open: { label: "Open", color: "info" },
    pending: { label: "Pending", color: "warning" },
    running: { label: "Running", color: "primary" },
    processing: { label: "Processing", color: "warning" },
    completed: { label: "Completed", color: "success" },
    cancelled: { label: "Cancelled", color: "error" },
};

const renderGameCurrencyAmount = (gameType, amount, size = 16) => {
    if (gameType === "playForMoney") return formatCash(amount || 0);
    if (gameType !== "playForGems" && gameType !== "playForSDCoins") {
        return formatPrice(amount || 0);
    }
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            {gameType === "playForGems" ? (
                <DiamondIcon sx={{ fontSize: size }} />
            ) : (
                <SDCoinIcon size={size} />
            )}
            {formatPrice(amount || 0)}
        </Box>
    );
};

const StatCard = ({ icon, label, value }) => (
    <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    </Card>
);

const RankCell = ({ rank }) => {
    const medal = MEDAL_COLORS[rank];
    return (
        <Box
            sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "0.8rem",
                bgcolor: medal || "transparent",
                color: medal ? "#fff" : "text.primary",
            }}
        >
            {rank}
        </Box>
    );
};

const LeaderboardScreen = () => {
    const { id } = useParams(); // Contest ID from URL
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [leaderboard, setLeaderboard] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [contestInfo, setContestInfo] = useState(null);
    const [searchItem, setSearchItem] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setDebouncedSearch(searchItem);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchItem]);

    useEffect(() => {
        fetchLeaderboard();
    }, [id, page, rowsPerPage, debouncedSearch]);

    const fetchLeaderboard = async () => {
        try {
            setLoading(true);
            const response = await contestServices.getLeaderboard({
                contestId: id,
                pageNumber: page,
                pageSize: rowsPerPage,
                searchItem: debouncedSearch || "",
            });

            const result = response?.data?.data || {};
            setLeaderboard(result.data || []);
            setTotalCount(result.totalCount || 0);
            setContestInfo({
                name: result.contestName || "",
                status: result.contestStatus || "",
                gameType: result.gameType || "",
                totalPayout: result.totalPayout || 0,
                totalRefund: result.totalRefund || 0,
                winnersCount: result.winnersCount || 0,
                numberOfWinners: result.numberOfWinners || 0,
            });
        } catch (error) {
            errorHandler(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event) => {
        setSearchItem(event.target.value);
        setPage(1); // Reset to first page when searching
    };

    const handlePlayerClick = (player) => {
        if (player.teamId) {
            navigate(`/contest-management/team-preview/${player.teamId}`);
        }
    };

    // Payouts only exist once the contest is settled, refunds once it's cancelled.
    const isSettled = contestInfo?.status === "completed";
    const isCancelled = contestInfo?.status === "cancelled";
    const showPayout = isSettled || isCancelled;
    const statusChip = STATUS_CHIP[contestInfo?.status];
    const statCols = showPayout ? 3 : 4;

    return (
        <CardLayout>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <EmojiEventsIcon sx={{ fontSize: 32, color: "primary.main" }} />
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Contest Leaderboard
                    </Typography>
                    {contestInfo?.name && (
                        <Typography variant="body2" color="text.secondary">
                            {contestInfo.name}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={statCols}>
                    <StatCard icon={<PeopleIcon />} label="Participants" value={totalCount} />
                </Grid>
                <Grid item xs={12} sm={6} md={statCols}>
                    <StatCard
                        icon={<MilitaryTechIcon />}
                        label={isSettled ? "Winners" : "Winner Slots"}
                        value={
                            isSettled
                                ? contestInfo?.winnersCount
                                : contestInfo?.numberOfWinners || "-"
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={statCols}>
                    <StatCard
                        icon={<FlagIcon />}
                        label="Status"
                        value={
                            statusChip ? (
                                <Chip
                                    label={statusChip.label}
                                    color={statusChip.color}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            ) : (
                                "-"
                            )
                        }
                    />
                </Grid>
                {showPayout && (
                    <Grid item xs={12} sm={6} md={statCols}>
                        <StatCard
                            icon={isCancelled ? <ReplayIcon /> : <PaidIcon />}
                            label={isCancelled ? "Total Refunds" : "Total Payouts"}
                            value={renderGameCurrencyAmount(
                                contestInfo?.gameType,
                                isCancelled ? contestInfo?.totalRefund : contestInfo?.totalPayout,
                                18
                            )}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Table */}
            <Card variant="outlined">
                <Box sx={{ p: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={searchItem}
                        onChange={handleSearchChange}
                        placeholder="Search by username, name or email"
                        sx={{ maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchItem && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear-search"
                                        onClick={() => {
                                            setSearchItem("");
                                            setDebouncedSearch("");
                                            setPage(1);
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap" } }}>
                                <TableCell sx={{ width: 70 }}>Rank</TableCell>
                                <TableCell>Player</TableCell>
                                <TableCell>Team Name</TableCell>
                                <TableCell align="right">Points</TableCell>
                                <TableCell align="right">
                                    {isCancelled ? "Refund" : "Payout"}
                                </TableCell>
                                <TableCell align="center" sx={{ width: 80 }}>
                                    Team
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={6}>
                                            <Skeleton height={32} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : leaderboard.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            {debouncedSearch
                                                ? `No participants found matching "${debouncedSearch}".`
                                                : "No participants yet."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                leaderboard.map((user, index) => {
                                    const rank = user.rank || index + 1;
                                    const amount = isCancelled ? user.refundData : user.payoutData;
                                    const hasAmount = Number(amount) > 0;

                                    return (
                                        <TableRow key={user.teamId || index} hover>
                                            <TableCell>
                                                <RankCell rank={rank} />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    <Avatar
                                                        src={user.image ? `${IMAGE_BASE}${user.image}` : undefined}
                                                        alt={user?.username}
                                                        sx={{ width: 32, height: 32 }}
                                                    />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                            {capitalizeFirstLetter(user.username)}
                                                        </Typography>
                                                        {(user.firstname || user.lastname) && (
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                {user.firstname} {user.lastname}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap>
                                                    {user.teamName || "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {user.gamepoints || 0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {showPayout ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            display: "inline-flex",
                                                            color: hasAmount
                                                                ? isCancelled
                                                                    ? "error.main"
                                                                    : "success.main"
                                                                : "text.secondary",
                                                        }}
                                                    >
                                                        {renderGameCurrencyAmount(contestInfo?.gameType, amount)}
                                                    </Typography>
                                                ) : (
                                                    <Tooltip title="Shown once the contest is settled" arrow>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Pending
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={user.teamId ? "Team Preview" : "No team"} arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            disabled={!user.teamId}
                                                            onClick={() => handlePlayerClick(user)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    onPageChange={(e, newPage) => setPage(newPage + 1)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(1);
                    }}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Card>
        </CardLayout>
    );
};

export default LeaderboardScreen;
